import type { AuthSession, UserEventAccess } from "@mvk/shared";
import { apiRequest } from "./client";
import { adminAuthStorage, visitorAuthStorage } from "./authStorage";

export const adminAuthApi = {
  async login(email: string, password: string): Promise<AuthSession> {
    const session = await apiRequest<AuthSession>("/auth/login", {
      method: "POST",
      body: { email, password },
      public: true,
    });
    adminAuthStorage.setToken(session.token);
    return session;
  },

  async me(): Promise<{ user: AuthSession["user"]; eventAccess: UserEventAccess[] }> {
    return apiRequest("/auth/me", { audience: "admin" });
  },

  logout(): void {
    adminAuthStorage.clearToken();
  },
};

export const visitorAuthApi = {
  async sendOtp(phone: string): Promise<{
    ok: boolean;
    devCode?: string;
    delivery?: "max" | "pending_bot" | "dev" | "none";
    message?: string;
    maxDeepLink?: string;
    loginToken?: string;
  }> {
    return apiRequest("/auth/phone/send-otp", {
      method: "POST",
      body: { phone },
      public: true,
    });
  },

  async verifyOtp(phone: string, code: string, claimCode?: string): Promise<AuthSession> {
    const session = await apiRequest<AuthSession>("/auth/phone/verify", {
      method: "POST",
      body: { phone, code, claimCode },
      public: true,
    });
    visitorAuthStorage.setToken(session.token);
    return session;
  },

  async maxInit(initData: string, claimCode?: string): Promise<AuthSession> {
    const session = await apiRequest<AuthSession>("/auth/max/init", {
      method: "POST",
      body: { initData, claimCode },
      public: true,
    });
    visitorAuthStorage.setToken(session.token);
    return session;
  },

  async maxDialogStatus(
    initData: string,
    opts?: { probe?: boolean },
  ): Promise<{ activated: boolean; activateDeepLink?: string }> {
    return apiRequest("/auth/max/dialog-status", {
      method: "POST",
      body: { initData, probe: opts?.probe ?? true },
      public: true,
    });
  },

  async maxContact(payload: {
    initData: string;
    phone: string;
    authDate?: string;
    hash?: string;
    legalConsents?: import("@mvk/shared").LegalConsentsPayload;
  }): Promise<AuthSession> {
    const session = await apiRequest<AuthSession>("/auth/max/contact", {
      method: "POST",
      body: {
        initData: payload.initData,
        phone: payload.phone,
        ...(payload.authDate ? { authDate: payload.authDate } : {}),
        ...(payload.hash ? { hash: payload.hash } : {}),
        ...(payload.legalConsents ? { legalConsents: payload.legalConsents } : {}),
      },
      public: true,
    });
    visitorAuthStorage.setToken(session.token);
    return session;
  },

  async badgeLogin(payload: {
    code: string;
    initData?: string;
    phone?: string;
    authDate?: string;
    hash?: string;
    legalConsents?: import("@mvk/shared").LegalConsentsPayload;
  }): Promise<AuthSession> {
    const session = await apiRequest<AuthSession>("/auth/badge-login", {
      method: "POST",
      body: payload,
      public: true,
    });
    visitorAuthStorage.setToken(session.token);
    return session;
  },

  async me(): Promise<{ user: AuthSession["user"]; eventAccess: UserEventAccess[] }> {
    return apiRequest("/auth/me", { audience: "visitor" });
  },

  /** Heartbeat so admin «сейчас в приложении» stays accurate while the tab is open. */
  async presence(): Promise<{ ok: boolean }> {
    return apiRequest("/auth/presence", { method: "POST", body: {}, audience: "visitor" });
  },

  async saveConsents(
    legalConsents: import("@mvk/shared").LegalConsentsPayload,
  ): Promise<{ user: AuthSession["user"]; eventAccess: UserEventAccess[] }> {
    return apiRequest("/auth/me/consents", {
      method: "POST",
      body: legalConsents,
      audience: "visitor",
    });
  },

  async saveCameraConsent(): Promise<{ user: AuthSession["user"] }> {
    return apiRequest("/auth/me/camera-consent", {
      method: "POST",
      body: {},
      audience: "visitor",
    });
  },

  async updateScanPrivacy(patch: {
    sharePhoneOnScan?: boolean;
    shareEmailOnScan?: boolean;
  }): Promise<{ user: AuthSession["user"] }> {
    return apiRequest("/auth/me/scan-privacy", {
      method: "PATCH",
      body: patch,
      audience: "visitor",
    });
  },

  logout(): void {
    visitorAuthStorage.clearToken();
  },
};

/** @deprecated use adminAuthApi */
export const authApi = adminAuthApi;
